import { useEffect, useMemo, useState } from 'react';

import {
  SEGMENTATION_API_BASE,
  parseSegmentationPayload,
} from '../utils/segmentation';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

const DEFAULTS = {
  loaded: true,
  showConcerns: true,
  showConditions: true,
  advancedCharts: true,
  productSuggestion: true,
  addToCart: true,
  productLink: true,
  allowedNames: null,
  aiGeneratedSummary: null,
  aiMode: null,
  productSuggestLimit: null,
};

/** @param {string} [appSessionToken] — brand-admin app_session_token (from report API, not skin flow session id). */
export function useSegmentationConfig(appSessionToken) {
  const resolvedToken = useMemo(
    () => String(appSessionToken || '').trim(),
    [appSessionToken],
  );

  const [config, setConfig] = useState(() => ({
    ...DEFAULTS,
    loaded: !resolvedToken,
  }));

  useEffect(() => {
    const token = resolvedToken;
    if (!token) {
      setConfig({ ...DEFAULTS, loaded: true });
      return undefined;
    }

    let cancelled = false;
    setConfig((prev) => ({ ...prev, loaded: false }));

    fetchWithTimeout(
      `${SEGMENTATION_API_BASE}/${encodeURIComponent(token)}/segmentation`,
      { timeoutMs: 20000 },
    )
      .then((res) => {
        if (!res.ok) throw new Error('segmentation unavailable');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setConfig({
          loaded: true,
          ...parseSegmentationPayload(data),
        });
      })
      .catch(() => {
        if (!cancelled) setConfig({ ...DEFAULTS, loaded: true });
      });

    return () => { cancelled = true; };
  }, [resolvedToken]);

  return config;
}
